import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Building,
  Zap,
  Calendar,
  Download,
  Filter,
  Eye
} from 'lucide-react';
import { reportsAPI } from '../services/api';
import { formatCurrency, getMonthName } from '../utils/helpers';
import toast from 'react-hot-toast';

const ReportsPage = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: '',
    report_type: 'monthly'
  });

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  useEffect(() => {
    fetchReportData();
  }, [filters]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.getSummary(filters);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  const downloadReport = async (type) => {
    try {
      const response = await reportsAPI.downloadReport(type, filters);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-report-${filters.year}${filters.month ? `-${filters.month}` : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
  <h3 className="text-lg font-medium text-secondary-900 dark:text-secondary-100 mb-2">No report data available</h3>
  <p className="text-secondary-600 dark:text-secondary-300">Please check your filters and try again</p>
      </div>
    );
  }

  const {
    summary,
    monthlyRevenue,
    paymentBreakdown,
    roomOccupancy,
    electricityConsumption,
    arrearsData
  } = reportData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Reports & Analytics</h1>
          <p className="text-secondary-600 dark:text-secondary-300">Comprehensive insights into your property management</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => downloadReport('financial')}
            className="btn-outline"
          >
            <Download className="w-4 h-4 mr-2" />
            Financial Report
          </button>
          <button
            onClick={() => downloadReport('occupancy')}
            className="btn-outline"
          >
            <Download className="w-4 h-4 mr-2" />
            Occupancy Report
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <Filter className="w-5 h-5 text-secondary-400 dark:text-secondary-300" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
            <div>
              <select
                name="report_type"
                value={filters.report_type}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="monthly">Monthly Report</option>
                <option value="yearly">Yearly Report</option>
                <option value="quarterly">Quarterly Report</option>
              </select>
            </div>
            <div>
              <select
                name="year"
                value={filters.year}
                onChange={handleFilterChange}
                className="form-input"
              >
                {generateYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                name="month"
                value={filters.month}
                onChange={handleFilterChange}
                className="form-input"
                disabled={filters.report_type === 'yearly'}
              >
                <option value="">All Months</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>
                    {getMonthName(month)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button
                onClick={() => setFilters({ year: new Date().getFullYear(), month: '', report_type: 'monthly' })}
                className="btn-outline w-full"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-300">Total Revenue</p>
              <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
                {formatCurrency(summary.totalRevenue)}
              </p>
              <div className="flex items-center mt-1">
                {summary.revenueChange >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span className={`text-sm ${summary.revenueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(summary.revenueChange)}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <Building className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-300">Occupancy Rate</p>
              <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
                {summary.occupancyRate}%
              </p>
              <p className="text-sm text-secondary-500 dark:text-secondary-300">
                {summary.occupiedRooms} of {summary.totalRooms} rooms
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Zap className="w-8 h-8 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-300">Electricity Revenue</p>
              <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
                {formatCurrency(summary.electricityRevenue)}
              </p>
              <p className="text-sm text-secondary-500 dark:text-secondary-300">
                {summary.totalUnitsConsumed} units consumed
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <Calendar className="w-8 h-8 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-300">Outstanding Arrears</p>
              <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
                {formatCurrency(summary.totalArrears)}
              </p>
              <p className="text-sm text-secondary-500 dark:text-secondary-300">
                {summary.roomsWithArrears} rooms affected
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card"
        >
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">Monthly Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="rent" stroke="#3B82F6" strokeWidth={2} name="Rent Revenue" />
              <Line type="monotone" dataKey="electricity" stroke="#F59E0B" strokeWidth={2} name="Electricity Revenue" />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Payment Breakdown Pie Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">Payment Type Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={paymentBreakdown}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {paymentBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Room Occupancy Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">Room Occupancy Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={roomOccupancy}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="room" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#10B981" name="Revenue Generated" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Electricity Consumption Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">Electricity Consumption by Room</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={electricityConsumption}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="room" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="units" fill="#F59E0B" name="Units Consumed" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Arrears Summary */}
      {arrearsData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">Outstanding Arrears</h3>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Room</th>
                  <th>Tenant</th>
                  <th>Type</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th>Days Overdue</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {arrearsData.map((arrear, index) => (
                  <tr key={index} className="hover:bg-secondary-50 dark:hover:bg-secondary-800">
                    <td>
                      <div className="flex items-center space-x-2">
                        <Building className="w-4 h-4 text-secondary-400 dark:text-secondary-300" />
                        <span className="font-medium text-secondary-900 dark:text-secondary-100">Room {arrear.room_number}</span>
                      </div>
                    </td>
                    <td className="text-secondary-900 dark:text-secondary-100">{arrear.tenant_name}</td>
                    <td>
                      <span className={`badge ${
                        arrear.type === 'rent' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {arrear.type === 'rent' ? 'Rent' : 'Electricity'}
                      </span>
                    </td>
                    <td>{arrear.due_date}</td>
                    <td className="font-medium text-red-600 dark:text-red-300">
                      {formatCurrency(arrear.amount)}
                    </td>
                    <td>
                      <span className={`badge ${
                        arrear.days_overdue > 30 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 
                        arrear.days_overdue > 15 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 
                        'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                      }`}>
                        {arrear.days_overdue} days
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        arrear.days_overdue > 30 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 
                        arrear.days_overdue > 15 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 
                        'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                      }`}>
                        {arrear.days_overdue > 30 ? 'Critical' : 
                         arrear.days_overdue > 15 ? 'Warning' : 'Overdue'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      <div className="card">
  <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => downloadReport('detailed')}
            className="p-4 text-left border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors bg-white dark:bg-secondary-900"
          >
            <Download className="w-6 h-6 text-primary-600 dark:text-primary-300 mb-2" />
            <h4 className="font-medium text-secondary-900 dark:text-secondary-100">Download Detailed Report</h4>
            <p className="text-sm text-secondary-600 dark:text-secondary-300">Complete financial and occupancy analysis</p>
          </button>
          
          <button
            onClick={() => downloadReport('arrears')}
            className="p-4 text-left border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors bg-white dark:bg-secondary-900"
          >
            <Eye className="w-6 h-6 text-yellow-600 dark:text-yellow-300 mb-2" />
            <h4 className="font-medium text-secondary-900 dark:text-secondary-100">Arrears Report</h4>
            <p className="text-sm text-secondary-600 dark:text-secondary-300">Outstanding payments and overdue analysis</p>
          </button>
          
          <button
            onClick={() => downloadReport('tax')}
            className="p-4 text-left border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors bg-white dark:bg-secondary-900"
          >
            <Calendar className="w-6 h-6 text-green-600 dark:text-green-300 mb-2" />
            <h4 className="font-medium text-secondary-900 dark:text-secondary-100">Tax Summary</h4>
            <p className="text-sm text-secondary-600 dark:text-secondary-300">Annual income summary for tax purposes</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
